/**
 * Sedes físicas de la institución.
 *
 * `Campus` es la ubicación geográfica; `Area` es la estructura académica. Un
 * programa puede impartirse en varias sedes.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type { Campus } from '@/interfaces/Campus'
import {
  getCampusService,
  getCampusByIdService,
  crearCampusService,
  updateCampusService,
  eliminarCampusService
} from '@/services/CampusService'

/** Store `campus`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionCampus = defineStore('campus', {
  state: () => ({
    campus: [] as Campus[],
    campusActual: null as Campus | null
  }),

  actions: {
    async cargarTodos(): Promise<void> {
      this.campus = await getCampusService()
    },

    async cargarPorId(id: number): Promise<Campus | null> {
      const data = await getCampusByIdService(id)
      this.campusActual = data
      return data
    },

    async crear(
      payload: Pick<Campus, 'nombre' | 'estado'>
    ): Promise<{ ok: boolean; data?: Campus; errors?: Record<string, string[]> }> {
      const result = await crearCampusService(payload)
      if (result.ok && result.data) {
        this.campus.push(result.data)
        this.campusActual = result.data
      }
      return result
    },

    async actualizar(
      id: number,
      payload: Partial<Pick<Campus, 'nombre' | 'estado'>>
    ): Promise<{ ok: boolean; data?: Campus; errors?: Record<string, string[]> }> {
      const result = await updateCampusService(id, payload)
      if (result.ok && result.data) {
        const idx = this.campus.findIndex(c => c.id_campus === id)
        if (idx !== -1) this.campus.splice(idx, 1, result.data)
        this.campusActual = result.data
      }
      return result
    },

    async eliminar(id: number): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
      const result = await eliminarCampusService(id)
      if (result.ok) {
        this.campus = this.campus.filter(c => c.id_campus !== id)
        if (this.campusActual?.id_campus === id) this.campusActual = null
      }
      return result
    }
  }
})
